import { NS_PER_S_BIGINT, strSecToBigIntNanosec } from "./time";

const NS_PER_MINUTE = 60n * NS_PER_S_BIGINT;
const NS_PER_HOUR = 60n * NS_PER_MINUTE;

/**
 * Duration utility for converting between seconds/nanoseconds and duration components.
 * Avoids precision loss and provides compatibility with Intl.DurationFormat.
 * Mimics Temporal.Duration API for future migration when it becomes baseline.
 */
export class Duration {
  private readonly hours: number;
  private readonly minutes: number;
  private readonly seconds: number;
  private readonly nanoseconds: number;

  private constructor(
    hours: number,
    minutes: number,
    seconds: number,
    nanoseconds: number,
  ) {
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
    this.nanoseconds = nanoseconds;
  }

  /**
   * Creates a Duration from either seconds or nanoseconds. A seconds input
   * never passes through a float nanosecond count, so durations beyond 2^53
   * nanoseconds (about 104 days) keep the precision the input carries.
   *
   * Seconds resolve to the nearest nanosecond. Magnitudes below one
   * nanosecond, along with zero, negative zero, NaN, and Infinity, produce
   * an all-zero Duration.
   *
   * A negative input, such as a plot tick before the event its panel is
   * aligned on, produces components that are all negative or zero;
   * Intl.DurationFormat requires every component to share one sign.
   */
  static from(input: { seconds: number } | { nanoseconds: bigint }): Duration {
    let totalNanos: bigint;

    if ("seconds" in input) {
      // Each branch below is picked by magnitude band, so convert the
      // magnitude and re-apply the sign once the total is known.
      const isNegative = input.seconds < 0;
      const magnitude = Math.abs(input.seconds);

      if (magnitude < 1e-9 || !Number.isFinite(magnitude)) {
        // The finite check also keeps NaN and Infinity away from the BigInt
        // conversions below, which throw on both.
        totalNanos = 0n;
      } else if (magnitude >= 1e21) {
        // toString switches to exponential notation at 1e21 and above
        // ("1e+21"), which strSecToBigIntNanosec cannot parse. Every float
        // that large is an exact integer, so BigInt converts it directly.
        totalNanos = BigInt(magnitude) * NS_PER_S_BIGINT;
      } else {
        // toString also switches to exponential notation below 1e-6
        // ("5e-7"); toFixed(9) renders those as a plain decimal already
        // rounded to the nearest nanosecond. From 1e-6 up, toString is
        // plain decimal itself, and strSecToBigIntNanosec rounds whatever
        // digits run past nanosecond precision.
        const plainDecimal =
          magnitude < 1e-6 ? magnitude.toFixed(9) : magnitude.toString();
        totalNanos = strSecToBigIntNanosec(plainDecimal);
      }

      if (isNegative) {
        totalNanos = -totalNanos;
      }
    } else {
      totalNanos = input.nanoseconds;
    }

    // BigInt division and remainder both truncate toward zero, so a negative
    // total gives components that are all negative or zero.
    const hours = totalNanos / NS_PER_HOUR;
    const nanosAfterHours = totalNanos % NS_PER_HOUR;

    const minutes = nanosAfterHours / NS_PER_MINUTE;
    const nanosAfterMinutes = nanosAfterHours % NS_PER_MINUTE;

    const seconds = nanosAfterMinutes / NS_PER_S_BIGINT;
    const nanoseconds = nanosAfterMinutes % NS_PER_S_BIGINT;

    return new Duration(
      Number(hours),
      Number(minutes),
      Number(seconds),
      Number(nanoseconds),
    );
  }

  /** Returns duration components for use with Intl.DurationFormat. */
  toObject(): {
    hours: number;
    minutes: number;
    seconds: number;
    nanoseconds: number;
  } {
    return {
      hours: this.hours,
      minutes: this.minutes,
      seconds: this.seconds,
      nanoseconds: this.nanoseconds,
    };
  }
}
